import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Wrench component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconWrench extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.19 22.78C3.62 22.78 3.04 22.56 2.59 22.13L1.71 21.29C1.26 20.86 1 20.28 1 19.65C1 19.02 1.23 18.44 1.68 17.99L9.7 9.97003C9.36 9.04003 9.23 8.04003 9.32 7.06003C9.44 5.81003 9.9 4.63003 10.66 3.63003C11.42 2.63003 12.44 1.87003 13.61 1.43003C14.79 0.990027 16.06 0.880027 17.28 1.13003C17.62 1.23003 17.87 1.37003 18.08 1.56003C18.29 1.75003 18.46 1.99003 18.57 2.24003C18.69 2.52003 18.63 2.85003 18.41 3.07003L15.29 6.19003C15.15 6.33003 15.06 6.54003 15.06 6.75003C15.06 6.96003 15.14 7.17003 15.29 7.32003L16.44 8.35003C16.56 8.47003 16.65 8.53003 16.75 8.57003C16.95 8.65003 17.18 8.66003 17.38 8.57003C17.48 8.53003 17.57 8.47003 17.65 8.39003L20.78 5.26003C21.03 5.01003 21.41 4.97003 21.7 5.15003C21.93 5.29003 22.13 5.48003 22.28 5.71003C22.43 5.93003 22.54 6.18003 22.59 6.45003C22.82 7.67003 22.72 8.92003 22.28 10.08C21.84 11.24 21.09 12.25 20.11 13.01C19.13 13.77 17.96 14.24 16.73 14.37C15.79 14.47 14.83 14.37 13.94 14.08L5.81 22.11C5.36 22.56 4.77 22.78 4.19 22.78V22.78ZM16 2.49003C15.38 2.49003 14.75 2.60003 14.15 2.83003C13.24 3.17003 12.45 3.76003 11.86 4.54003C11.27 5.31003 10.91 6.23003 10.82 7.20003C10.74 8.10003 10.89 9.01003 11.27 9.84003C11.4 10.13 11.34 10.46 11.12 10.68L2.74 19.06C2.58 19.22 2.5 19.42 2.5 19.64C2.5 19.86 2.59 20.06 2.75 20.22L3.63 21.06C3.95 21.37 4.45 21.36 4.76 21.06L13.23 12.67C13.44 12.46 13.77 12.39 14.04 12.51C14.84 12.84 15.72 12.97 16.58 12.87C17.54 12.77 18.44 12.4 19.2 11.81C19.96 11.22 20.54 10.44 20.88 9.54003C21.19 8.73003 21.29 7.85003 21.17 6.99003L18.73 9.43003C18.52 9.65003 18.27 9.82003 17.98 9.94003C17.4 10.18 16.75 10.18 16.17 9.94003C15.88 9.82003 15.63 9.64003 15.41 9.42003L14.26 8.40003C13.8 7.93003 13.56 7.36003 13.56 6.75003C13.56 6.14003 13.79 5.57003 14.22 5.13003L16.79 2.56003C16.53 2.52003 16.26 2.49003 15.99 2.49003H16Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-wrench') === undefined) {
  customElements.define('cat-icon-wrench', BlocksIconWrench);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-wrench': BlocksIconWrench;
  }
}
